import { useState, useEffect, useCallback } from "react";
import { Link2, Loader2, Search, Cloud } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { formatDate } from "../../utils/format";
import { normalizar } from "../../components/SelectBuscable";
import { STATUS_LABELS } from "./constants";
import { VincularRetroactivoModal } from "./PedidosMaterialesTab";
import { VincularPSRetroactivoModal } from "../ComprasServicios";

/* Fix EF (2026-09-24): "el 'Formateo Ptos Aprobados' ya no tiene utilidad, queremos
   cambiar a 'Vinculación de Pedidos Rápidos'. Acá deben caer todos los Pedidos Rápidos
   pendientes de vinculación a presupuesto para realizar el cruce… de manera retroactiva
   con todos los pedidos rápidos". Pedidos de materiales y de servicios de obra rápida
   que todavía no tienen presupuesto. "Vincular" es el mismo de cada pedido: propaga el
   presupuesto a toda la cadena (PP, AP, PF, OC / OTS). */
const opts = { withCredentials: true };
const TIPOS = [["todos", "Todos"], ["materiales", "Materiales"], ["servicios", "Servicios"]];

export default function VinculacionPedidosRapidos() {
  const [data, setData] = useState(null);
  const [listas, setListas] = useState({ clientes: [], obras: [], budgets: [] });
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [target, setTarget] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/purchases/pedidos-rapidos-pendientes`, opts)
      .then(({ data }) => setData(data || []))
      .catch(err => { toast.error(err.response?.data?.detail || "No se pudieron cargar los pedidos rápidos"); setData([]); });
  }, []);
  useEffect(() => {
    load();
    Promise.all([
      axios.get(`${API}/listas/clientes`, opts), axios.get(`${API}/listas/obras`, opts), axios.get(`${API}/budgets?light=true`, opts),
    ]).then(([c, o, b]) => setListas({ clientes: c.data || [], obras: o.data || [], budgets: b.data || [] }))
      .catch(() => toast.error("No se pudieron cargar clientes, obras y presupuestos"));
  }, [load]);

  const filas = (data || []).filter(p => (tipo === "todos" || p.tipo === tipo)
    && (!q.trim() || normalizar(`${p.number} ${p.obra_rapida_nombre} ${p.requested_by_name} ${p.detalle}`).includes(normalizar(q.trim()))));
  const cerrar = () => setTarget(null);
  const vinculado = () => { setTarget(null); load(); };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="vinculacion-pedidos-rapidos">
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Cloud size={20} className="text-sky-600" /> Vinculación de Pedidos Rápidos</h1>
        <p className="text-xs text-gray-500 mt-0.5">Pedidos de obra rápida (materiales y servicios) pendientes de vincular a un presupuesto aprobado. Al vincular, el presupuesto pasa a todos sus documentos (PP, AP, PF, OC / OTS).</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar N°, obra, solicitante o material…"
            className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm" data-testid="vpr-buscar" />
        </div>
        <div className="flex gap-1.5">
          {TIPOS.map(([k, l]) => (
            <button key={k} onClick={() => setTipo(k)} data-testid={`vpr-tipo-${k}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-xl border ${tipo === k ? "bg-sky-600 text-white border-sky-600" : "bg-white text-gray-600 border-gray-200"}`}>
              {l}{k !== "todos" ? ` (${(data || []).filter(p => p.tipo === k).length})` : ""}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-x-auto">
        {!data ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div> : (
          <table className="w-full text-sm" data-testid="vpr-tabla">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase"><tr>
              <th className="text-left px-4 py-2.5">Tipo</th><th className="text-left px-4 py-2.5">N°</th><th className="text-left px-4 py-2.5">Obra rápida</th>
              <th className="text-left px-4 py-2.5">Detalle</th><th className="text-left px-4 py-2.5">Solicitado por</th><th className="text-left px-4 py-2.5">Fecha</th>
              <th className="text-left px-4 py-2.5">Estado</th><th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {filas.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-gray-400">{(data || []).length ? "Sin coincidencias" : "No hay pedidos rápidos pendientes de vinculación"}</td></tr>}
              {filas.map(p => (
                <tr key={`${p.tipo}-${p._id}`} className="border-t border-gray-50 hover:bg-sky-50/40" data-testid={`vpr-fila-${p._id}`}>
                  <td className="px-4 py-2.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.tipo === "servicios" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>{p.tipo === "servicios" ? "Servicios" : "Materiales"}</span></td>
                  <td className="px-4 py-2.5 font-mono text-xs font-bold">{p.number}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{p.obra_rapida_nombre || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[260px] truncate" title={p.detalle}>{p.items} ítem(s){p.detalle ? ` · ${p.detalle}` : ""}</td>
                  <td className="px-4 py-2.5 text-gray-600">{p.requested_by_name || "—"}</td>
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{formatDate(p.created_at)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">{STATUS_LABELS[p.status] || p.status || "—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => setTarget(p)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold rounded-lg" data-testid={`vpr-vincular-${p._id}`}>
                      <Link2 size={12} /> Vincular
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <VincularRetroactivoModal target={target?.tipo === "materiales" ? target : null} onClose={cerrar}
        listasClientes={listas.clientes} listasObras={listas.obras} allBudgets={listas.budgets} onLinked={vinculado} />
      <VincularPSRetroactivoModal target={target?.tipo === "servicios" ? target : null} onClose={cerrar}
        listasClientes={listas.clientes} listasObras={listas.obras} allBudgets={listas.budgets} onLinked={vinculado} />
    </div>
  );
}
